import DOMPurify from 'dompurify';
import { formatDistanceToNow } from 'date-fns';
import { withNamespaces } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, CardBody, CardImg, CardText, CardTitle, Col, Row } from 'reactstrap';

import useSupplierOverviewLatestFindings from './hooks/useSupplierOverviewLatestFindings';

const LatestFindingsSkeleton = () => (
    <>
        <div className="dt-field dt-skeleton mb-2" style={{ width: '20%' }} />
        <Row>
            <Col sm="12" md="3">
                <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
            </Col>
            <Col sm="12" md="3">
                <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
            </Col>
            <Col sm="12" md="3">
                <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
            </Col>
            <Col sm="12" md="3">
                <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
            </Col>
        </Row>
    </>
);

const LatestFindings = ({
    t,
    supplierId,
}) => {
    const location = useLocation();
    const history = useHistory();

    const fetchLatestFindingsQuery = useSupplierOverviewLatestFindings({
        supplierId,
        t,
    });

    const goToFindingDetailPage = ({ id }) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', 'findings');
        searchParams.set('id', id);
        history.push({
            search: searchParams.toString(),
        });
    };

    if (fetchLatestFindingsQuery.isLoading || fetchLatestFindingsQuery.isFetching) {
        return <LatestFindingsSkeleton />;
    }

    const latestFindings = Array.isArray(fetchLatestFindingsQuery.data)
        ? fetchLatestFindingsQuery.data
        : [];

    if (!latestFindings.length) {
        return null;
    }

    return (
        <Card className="latest-finding-section">
            <CardBody>
                <CardTitle className="overview-section-title mb-4">
                    {t('Latest Findings')}
                </CardTitle>

                <Row>
                    {latestFindings.map((finding) => (
                        <Col key={finding.id} sm="12" md="3">
                            <Card className="shadow-none mb-0">
                                <CardImg
                                    top
                                    src={`/images/findings/${finding.image}.jpg`}
                                    alt={finding.title}
                                />

                                <CardBody>
                                    <CardText className="mb-2">
                                        <small className="category">
                                            {finding.news_agency}
                                        </small>
                                    </CardText>

                                    <CardTitle
                                        onClick={() => goToFindingDetailPage(finding)}
                                        className="d-flex align-items-center"
                                    >
                                        {finding.weight ? (
                                            <i
                                                style={{ color: `#${finding.weight}` }}
                                                className="ri-checkbox-blank-circle-fill me-2"
                                            />
                                        ) : null}
                                        <p className="m-0 ellipsis l-3">{finding.title}</p>
                                    </CardTitle>

                                    <CardText
                                        className="mb-2 ellipsis l-3"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(finding.lead_text),
                                        }}
                                    />

                                    <CardText className="mb-1">
                                        <small className="last-update">
                                            {t('Last updated')}{' '}
                                            {formatDistanceToNow(
                                                new Date(finding?.release_date?.timestamp * 1000),
                                                { addSuffix: true }
                                            )}
                                        </small>
                                    </CardText>

                                    <div className="key-words">
                                        <span className="badge me-1">{finding?.keyword}</span>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(LatestFindings);
